import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Calendar, User, ArrowRight, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import SEOHead from '../components/SEOHead';
import VideoCarousel from '../components/VideoCarousel';
import { getReadingTime } from '../lib/readingTime';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Blog = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentLang = location.pathname.startsWith('/de') ? 'de' : 'en';
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const seoContent = {
    en: {
      title: 'Podcast Zurich Blog | Tips, Insights & Stories',
      description: 'Read the latest insights, tips, and stories from the Podcast Zurich team. Learn about podcast production, video podcasts, content creation, and more.',
    },
    de: {
      title: 'Podcast Zürich Blog | Tipps, Einblicke & Geschichten',
      description: 'Lesen Sie die neuesten Einblicke, Tipps und Geschichten vom Podcast Zürich Team. Erfahren Sie mehr über Podcast-Produktion, Video-Podcasts und Content-Erstellung.',
    }
  };

  useEffect(() => {
    fetchPosts();
  }, [currentLang]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/blog/posts`, {
        params: {
          lang: currentLang,
          published_only: true,
          limit: 20,
        },
      });
      setPosts(response.data);
    } catch (error) {
      console.error('Failed to fetch blog posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const content = {
    en: {
      title: 'Latest Posts',
      subtitle: 'Insights, updates, and stories from the Podcast Zurich team',
      readMore: 'Read More',
      loading: 'Loading posts...',
      noPosts: 'No blog posts available yet. Check back soon!',
    },
    de: {
      title: 'Neueste Beiträge',
      subtitle: 'Einblicke, Updates und Geschichten vom Podcast Zurich Team',
      readMore: 'Weiterlesen',
      loading: 'Beiträge werden geladen...',
      noPosts: 'Noch keine Blogbeiträge verfügbar. Schauen Sie bald wieder vorbei!',
    },
  };

  const t = content[currentLang];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return currentLang === 'en'
      ? date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
      : date.toLocaleDateString('de-DE', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const handlePostClick = (slug) => {
    navigate(`/${currentLang}/blog/${slug}`);
  };

  // Display loading state
  if (loading) {
    return (
      <>
        <SEOHead 
          title={seoContent[currentLang].title}
          description={seoContent[currentLang].description}
          canonicalPath={`/${currentLang}/blog`}
          ogImage="https://customer-assets.emergentagent.com/job_podcast-hub-22/artifacts/re8fiacy_DSC05748.jpg"
        />
        <main className="min-h-screen pt-32 pb-20 px-6 bg-gradient-to-br from-gray-100 to-gray-200">
          <div className="max-w-7xl mx-auto">
            <div className="text-center">
              <h1 className="text-5xl md:text-6xl font-bold mb-6">{t.title}</h1>
              <p className="text-xl text-gray-600">{t.loading}</p>
            </div>
          </div>
        </main>
      </>
    );
  }

  // Display posts or empty state
  const displayPosts = posts.length > 0 ? posts.map(post => ({
    id: post.id,
    slug: post.slug,
    title: post.title[currentLang],
    excerpt: post.excerpt[currentLang],
    author: post.author,
    date: post.created_at,
    image: post.image_url,
    readingTime: getReadingTime(post.content?.[currentLang] || '', currentLang),
  })) : [];

  return (
    <>
      <SEOHead 
        title={seoContent[currentLang].title}
        description={seoContent[currentLang].description}
        canonicalPath={`/${currentLang}/blog`}
        ogImage="https://customer-assets.emergentagent.com/job_podcast-hub-22/artifacts/re8fiacy_DSC05748.jpg"
      />
      
      {/* Video Carousel Section */}
      <div className="pt-20">
        <VideoCarousel currentLang={currentLang} />
      </div>
      
      <main className="min-h-screen pb-20 px-6 bg-gradient-to-br from-gray-100 to-gray-200" role="main">
        <div className="max-w-7xl mx-auto pt-16">
          <header className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">{t.title}</h1>
            <p className="text-xl text-gray-600">{t.subtitle}</p>
          </header>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" role="list">
            {displayPosts.length > 0 ? (
              displayPosts.map((post) => (
                <Card 
                  key={post.id} 
                  className="overflow-hidden hover:shadow-xl transition-shadow group cursor-pointer"
                  onClick={() => handlePostClick(post.slug)}
                  role="listitem"
                  data-testid={`blog-post-${post.slug}`}
                >
                  <figure className="aspect-video overflow-hidden bg-gray-200">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                  </figure>
                  <CardContent className="p-6">
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500 mb-3">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" aria-hidden="true" />
                        <time dateTime={post.date}>{formatDate(post.date)}</time>
                      </div>
                      <div className="flex items-center gap-1">
                        <User className="w-4 h-4" aria-hidden="true" />
                        <span>{post.author}</span>
                      </div>
                      <div className="flex items-center gap-1" data-testid={`reading-time-${post.slug}`}>
                        <Clock className="w-4 h-4" aria-hidden="true" />
                        <span>{post.readingTime}</span>
                      </div>
                    </div>
                    <h2 className="text-xl font-bold mb-3 line-clamp-2">{post.title}</h2>
                    <p className="text-gray-600 mb-4 line-clamp-3">{post.excerpt}</p>
                    <Button
                      variant="ghost"
                      className="p-0 h-auto font-semibold hover:bg-transparent"
                      style={{ color: '#000' }}
                    >
                      {t.readMore}
                      <ArrowRight className="ml-2 w-4 h-4" aria-hidden="true" />
                    </Button>
                  </CardContent>
                </Card>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <p className="text-xl text-gray-600">{t.noPosts}</p>
              </div>
            )}
          </div>

          {displayPosts.length === 0 && (
            <div className="mt-16 text-center p-12 bg-white rounded-lg">
              <div className="aspect-video max-w-3xl mx-auto rounded-lg overflow-hidden shadow-lg">
                <iframe
                  src="https://player.vimeo.com/video/1132614767?h=62c266d920&badge=0&autopause=0&player_id=0&app_id=58479"
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
                  style={{ width: '100%', height: '100%' }}
                  title="Latest from Podcast Zurich"
                  loading="lazy"
                ></iframe>
              </div>
            </div>
          )}
        </div>
      </main>
    </>
  );
};

export default Blog;